/* Entity-Vorgaben für Half-Life und Opposing Force (Auswahl der gebräuchlichsten Klassen). */
/** Entity-Klasse: k = Punkt- oder Brush-Entity, g = Gruppe (Farbe, Ebene), d = Standardwerte */
export interface EntDef {
  c: string; k: 'point' | 'brush'; g: string; color: string; d: Record<string, string>;
  /** aus einer FGD: Box in Einheiten (min, max), Modell (leer = Feld model), Symbol-Sprite, Beschreibung */
  size?: [number[], number[]]; studio?: string; iconsprite?: string; sprite?: string; desc?: string; fgd?: boolean;
}

const EntDefs = (function () {
  'use strict';
  const C: Record<string, string> = { player: '#46c98b', light: '#f0b400', monster: '#f0405e', item: '#3b8fff', trigger: '#ff9f1a', func: '#c07cff', other: '#8795a3' };
  const L: EntDef[] = [];
  const add = (c: string, k: EntDef['k'], g: string, d?: Record<string, string>) => L.push({ c: c, k: k, g: g, color: C[g] || C.other, d: d || {} });

  ['info_player_start', 'info_player_deathmatch', 'info_player_team1', 'info_player_team2', 'info_ctfspawn'].forEach((c) => add(c, 'point', 'player', { angles: '0 0 0' }));
  add('info_teleport_destination', 'point', 'player', { angles: '0 0 0', targetname: '' });
  add('info_landmark', 'point', 'other', { targetname: '' });
  add('info_target', 'point', 'other', { targetname: '' });
  add('light', 'point', 'light', { _light: '255 255 255 200' });
  add('light_spot', 'point', 'light', { _light: '255 255 255 200', angles: '0 0 0', pitch: '-90', _cone: '30', _cone2: '45' });
  add('light_environment', 'point', 'light', { _light: '255 255 255 200', angles: '0 0 0', pitch: '-90' });

  ['weapon_crowbar', 'weapon_9mmhandgun', 'weapon_9mmAR', 'weapon_357', 'weapon_shotgun', 'weapon_crossbow', 'weapon_rpg', 'weapon_gauss', 'weapon_egon',
    'weapon_hornetgun', 'weapon_handgrenade', 'weapon_satchel', 'weapon_tripmine', 'weapon_snark',
    'weapon_eagle', 'weapon_knife', 'weapon_pipewrench', 'weapon_grapple', 'weapon_m249', 'weapon_sniperrifle', 'weapon_sporelauncher', 'weapon_displacer', 'weapon_shockrifle', 'weapon_penguin']
    .forEach((c) => add(c, 'point', 'item'));
  ['ammo_9mmclip', 'ammo_9mmAR', 'ammo_9mmbox', 'ammo_ARgrenades', 'ammo_357', 'ammo_buckshot', 'ammo_crossbow', 'ammo_rpgclip', 'ammo_gaussclip', 'ammo_556', 'ammo_762', 'ammo_spore']
    .forEach((c) => add(c, 'point', 'item'));
  ['item_healthkit', 'item_battery', 'item_suit', 'item_longjump', 'item_antidote', 'item_security'].forEach((c) => add(c, 'point', 'item'));

  ['monster_human_grunt', 'monster_alien_grunt', 'monster_alien_slave', 'monster_barnacle', 'monster_bullchicken', 'monster_headcrab', 'monster_houndeye',
    'monster_scientist', 'monster_barney', 'monster_zombie', 'monster_gargantua', 'monster_turret', 'monster_miniturret', 'monster_sentry', 'monster_apache',
    'monster_osprey', 'monster_tentacle', 'monster_leech', 'monster_babycrab', 'monster_bigmomma', 'monster_nihilanth', 'monster_cockroach', 'monster_rat',
    'monster_gman', 'monster_human_assassin', 'monster_shocktrooper', 'monster_pitdrone', 'monster_pitworm', 'monster_gonome', 'monster_voltigore',
    'monster_babygarg', 'monster_male_assassin', 'monster_op4loader', 'monster_otis', 'monster_human_grunt_ally', 'monster_human_medic_ally',
    'monster_human_torch_ally', 'monster_zombie_barney', 'monster_zombie_soldier', 'monster_geneworm', 'monster_penguin']
    .forEach((c) => add(c, 'point', 'monster', { angles: '0 0 0' }));

  ['func_wall', 'func_illusionary', 'func_door', 'func_door_rotating', 'func_button', 'func_breakable', 'func_conveyor', 'func_ladder', 'func_pushable',
    'func_rotating', 'func_train', 'func_plat', 'func_water', 'func_friction', 'func_rot_button', 'func_healthcharger', 'func_recharge', 'func_tracktrain']
    .forEach((c) => add(c, 'brush', 'func', c === 'func_door' ? { angle: '-1', speed: '100', wait: '4', lip: '2' } : {}));
  ['trigger_multiple', 'trigger_once', 'trigger_hurt', 'trigger_push', 'trigger_teleport', 'trigger_changelevel', 'trigger_auto', 'trigger_relay',
    'trigger_counter', 'trigger_gravity', 'trigger_transition', 'trigger_cdaudio']
    .forEach((c) => add(c, 'brush', 'trigger', c === 'trigger_hurt' ? { dmg: '10' } : {}));

  ['ambient_generic', 'env_sprite', 'env_beam', 'env_explosion', 'env_shake', 'env_fade', 'env_message', 'env_spark', 'env_sound', 'env_glow', 'env_laser',
    'game_text', 'game_player_equip', 'multi_manager', 'path_corner', 'info_node', 'cycler', 'infodecal', 'trigger_camera', 'target_cdaudio']
    .forEach((c) => add(c, 'point', 'other'));

  add('env_fog', 'point', 'other', { rendercolor: '128 128 128', startdist: '500', enddist: '1500' });
  const builtin = new Map(L.map((d): [string, EntDef] => [d.c, d]));
  // Klassen aus geladenen FGDs ersetzen gleichnamige eingebaute und ergänzen die Liste
  let ext = new Map<string, EntDef>();
  let merged: EntDef[] = L;
  /** Gruppe (Farbe, Menü) nach dem Namen der Klasse */
  const groupOf = (c: string): string => /^trigger_/.test(c) ? 'trigger' : /^func_/.test(c) ? 'func' : /^monster_/.test(c) ? 'monster' : /^(weapon_|ammo_|item_)/.test(c) ? 'item' : /^light/.test(c) ? 'light' : /^info_player/.test(c) ? 'player' : 'other';
  function get(c: string): EntDef {
    const known = ext.get(c) || builtin.get(c);
    if (known) return known;
    const k: EntDef['k'] = /^(func_|trigger_)/.test(c) ? 'brush' : 'point';
    const g = groupOf(c);
    return { c: c, k: k, g: g, color: C[g], d: {} };
  }
  /** Klassen aus FGDs einsetzen (null oder leer: nur die eingebauten) */
  function use(defs: EntDef[] | null): void {
    ext = new Map((defs || []).map((d): [string, EntDef] => [d.c, d]));
    merged = ext.size ? L.filter((d) => !ext.has(d.c)).concat(Array.from(ext.values())) : L;
  }
  /** Entity-Art mit Symbol statt Würfel: 'sound' für alles, was Ton abspielt oder Tonzonen setzt. */
  const iconKind = (c?: string): 'sound' | null => (/^(ambient_generic|env_sound|speaker|target_cdaudio|trigger_cdaudio)$/.test(c || '') ? 'sound' : null);
  /** Hörweite von ambient_generic in Einheiten (Näherung aus den Radius-Flags der Engine: klein/mittel/groß; 'überall' = 0). */
  function soundRadius(kv: Record<string, string>): number {
    const f = parseInt(kv.spawnflags, 10) || 0;
    if (f & 1) return 0; if (f & 8) return 1250; if (f & 4) return 800; if (f & 2) return 500; return 800;
  }
  /** Zeichnet ein Symbol (Lautsprecher mit Schallwellen) mittig bei (x,y) in Kantenlänge size. */
  function drawIcon(ctx: CanvasRenderingContext2D, kind: string | null, x: number, y: number, size: number, color: string, bg?: string | null): void
  {
      const u = size / 2;
      ctx.save(); ctx.translate(x, y); ctx.lineJoin = 'round'; ctx.lineCap = 'round';
      if (bg) { ctx.fillStyle = bg; ctx.beginPath(); ctx.arc(0, 0, u * 1.08, 0, 6.2832); ctx.fill(); }
      ctx.fillStyle = color; ctx.strokeStyle = color; ctx.lineWidth = Math.max(1.3, size / 11);
      if (kind === 'sound') {
        ctx.beginPath();
        ctx.moveTo(-0.78 * u, -0.28 * u); ctx.lineTo(-0.40 * u, -0.28 * u); ctx.lineTo(0.06 * u, -0.66 * u); ctx.lineTo(0.06 * u, 0.66 * u); ctx.lineTo(-0.40 * u, 0.28 * u); ctx.lineTo(-0.78 * u, 0.28 * u);
        ctx.closePath(); ctx.fill();
        ctx.beginPath(); ctx.arc(0.06 * u, 0, 0.42 * u, -0.9, 0.9); ctx.stroke();
        ctx.beginPath(); ctx.arc(0.06 * u, 0, 0.80 * u, -0.9, 0.9); ctx.stroke();
      }
      ctx.restore();
    }
  return {
    get list(): EntDef[] { return merged; },
    colors: C,
    get byClass(): Map<string, EntDef> { return new Map(merged.map((d): [string, EntDef] => [d.c, d])); },
    use,
    groupOf,
    /** In der eingebauten Liste (Half-Life, Opposing Force)? Alles andere stammt aus einer FGD, ist also mod-eigen. */
    isBuiltin: (c: string): boolean => builtin.has(c),
    get,
    iconKind,
    soundRadius,
    drawIcon,
    colorFor: (c?: string): string => get(c || '').color,
    isBrushClass: (c?: string): boolean => get(c || '').k === 'brush',
    pointClasses: (): EntDef[] => merged.filter((d) => d.k === 'point'),
    brushClasses: (): EntDef[] => merged.filter((d) => d.k === 'brush')
  };
})();
export default EntDefs;
